import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Eye, CheckCircle2, XCircle, MessageSquare, Clock, UserPlus, Flag,
  AlertTriangle, History, RefreshCw,
} from "lucide-react";

/**
 * Per-participant partner-action timeline (participant-console-007).
 *
 * STANDALONE component: 004's consolidated review pane imports it LATER as a
 * self-contained section (`<ActivityTimeline userId={...} />`). It is not yet
 * mounted anywhere, so the CRA build does not exercise it — but `yarn lint`
 * does, hence the file is kept lint-clean on its own.
 *
 * Consumes 007's endpoint:
 *   GET /api/partner/console/participants/:userId/activity
 *   -> { user_id, count, rows: [partner_console_audit record, ...] }   // newest-first
 *
 * Each row is a record from the DEDICATED `partner_console_audit` stream —
 * NEVER the staff command-center audit. The component renders the partner
 * principal, the action, any status transition, the reason, and the timestamp.
 */

// Maps the backend partner-action vocabulary (services/partner_audit.PARTNER_ACTIONS)
// to an icon + human label + accent. Unknown actions fall back gracefully.
const ACTION_META = {
  view:           { Icon: Eye,          label: "Viewed",            accent: "text-slate-500" },
  countersign:    { Icon: CheckCircle2, label: "Countersigned",     accent: "text-emerald-600" },
  reject:         { Icon: XCircle,      label: "Rejected",          accent: "text-red-600" },
  "request-info": { Icon: MessageSquare,label: "Requested info",    accent: "text-amber-600" },
  snooze:         { Icon: Clock,        label: "Snoozed",           accent: "text-slate-500" },
  assign:         { Icon: UserPlus,     label: "Assigned",          accent: "text-blue-600" },
  flag:           { Icon: Flag,         label: "Flagged",           accent: "text-amber-600" },
};

const actionMeta = (action) =>
  ACTION_META[action] || { Icon: History, label: action || "Action", accent: "text-slate-500" };

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const ActivityTimeline = ({ userId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) {
      // No participant in context — clear out and stop loading so the UI can
      // render an empty state instead of being stuck on the loading spinner.
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(
        `/partner/console/participants/${encodeURIComponent(userId)}/activity`,
      );
      setRows(r.data?.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load activity history.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="activity-timeline">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <History size={15} className="text-slate-500" />
          Activity history
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading || !userId}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-50"
          data-testid="activity-refresh"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-4" data-testid="activity-loading">
          Loading activity history...
        </p>
      ) : error ? (
        <div className="py-4 text-sm" data-testid="activity-error">
          <AlertTriangle className="inline text-amber-500 mr-1.5 -mt-0.5" size={16} />
          <span className="text-slate-700">{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-4" data-testid="activity-empty">
          No partner activity recorded for this participant yet.
        </p>
      ) : (
        <ol className="relative border-l border-slate-200 ml-2 space-y-4" data-testid="activity-list">
          {rows.map((r) => {
            const { Icon, label, accent } = actionMeta(r.action);
            const transitioned =
              r.new_status && r.previous_status && r.new_status !== r.previous_status;
            return (
              <li
                key={r.id}
                className="ml-4"
                data-testid={`activity-row-${r.id}`}
              >
                <span className="absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-white ring-1 ring-slate-200">
                  <Icon size={12} className={accent} />
                </span>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-sm font-medium text-slate-900">{label}</span>
                  <span className="text-xs text-slate-500">
                    {r.partner_name || r.partner_email || "Partner"}
                  </span>
                  <time className="text-xs text-slate-400" title={r.created_at || ""}>
                    {fmtDateTime(r.created_at)}
                  </time>
                </div>
                {transitioned && (
                  <p className="text-xs text-slate-600 mt-0.5">
                    <span className="text-slate-400">{r.previous_status}</span>
                    {" → "}
                    <span className="font-medium">{r.new_status}</span>
                  </p>
                )}
                {r.reason && (
                  <p className="text-xs text-slate-600 mt-0.5 italic">“{r.reason}”</p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default ActivityTimeline;
